import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Shield, CheckCircle, Warning, XCircle, ArrowSquareOut, CurrencyDollar } from "@phosphor-icons/react";

// Priority groups — lower index = higher priority.
const GROUPS = [
  {
    id: "p0",
    label: "P0 · Required for production",
    summary: "Without these set, the platform cannot accept real money or even start up cleanly.",
    accent: "border-[#D96C4A] bg-[#D96C4A]/5 text-[#B04E2F]",
    chip: "bg-[#D96C4A]/15 text-[#B04E2F]",
    keys: ["jwt", "stripe", "base_url", "bootstrap"],
  },
  {
    id: "p1",
    label: "P1 · Operate & monitor",
    summary: "Required to actually run customer-facing flows end-to-end (emails, SMS, fraud monitoring).",
    accent: "border-[#CBA35C] bg-[#CBA35C]/5 text-[#8C6B21]",
    chip: "bg-[#CBA35C]/15 text-[#8C6B21]",
    keys: ["resend", "twilio", "abuse"],
  },
  {
    id: "p2",
    label: "P2 · Premium integrations",
    summary: "Optional upgrades — receivers can still use cards without these, but Add-to-Wallet and automated Stripe Outbound require them.",
    accent: "border-[#184A30] bg-[#184A30]/5 text-[#184A30]",
    chip: "bg-[#184A30]/15 text-[#184A30]",
    keys: ["apple_wallet", "google_wallet", "global_payouts"],
  },
];

function StatusIcon({ status, size = 22 }) {
  if (status === "ok") return <CheckCircle size={size} weight="fill" className="text-emerald-600" />;
  if (status === "warn") return <Warning size={size} weight="fill" className="text-amber-600" />;
  return <XCircle size={size} weight="fill" className="text-rose-600" />;
}

// Inline smoke-test trigger for Apple/Google Wallet credentials.
// Hits the new /admin/wallet/test-{provider} endpoints, surfaces the structured
// failure stage (env_check / p12_open / jwt_sign / pkpass_build / done) inline.
function WalletSmokeTest({ provider }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const label = provider === "apple" ? "Apple Wallet" : "Google Wallet";

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post(`/admin/wallet/test-${provider}`);
      setResult(r.data);
    } catch (e) {
      setResult({
        ok: false,
        stage: "request_failed",
        error: e?.response?.data?.detail || e?.message || "Request failed",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3" data-testid={`wallet-smoke-${provider}`}>
      <Button
        variant="outline"
        size="sm"
        onClick={run}
        disabled={busy}
        className="rounded-xl border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5 text-xs"
        data-testid={`wallet-smoke-${provider}-btn`}
      >
        {busy ? "Testing…" : `Test ${label} credentials`}
      </Button>
      {result && (
        <div
          className={`mt-2 rounded-lg border p-3 text-xs ${
            result.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-rose-50 border-rose-200 text-rose-900"
          }`}
          data-testid={`wallet-smoke-${provider}-result`}
        >
          <p className="font-semibold mb-1">
            {result.ok ? `✓ ${label} credentials are valid` : `✗ Failed at stage: ${result.stage || "unknown"}`}
          </p>
          {result.error && (
            <p className="mono break-all opacity-90">{result.error}</p>
          )}
          {result.missing && Array.isArray(result.missing) && result.missing.length > 0 && (
            <p className="mono opacity-90">Missing: {result.missing.join(", ")}</p>
          )}
          {result.ok && provider === "apple" && (
            <ul className="mt-1 space-y-0.5 opacity-90">
              {result.cert_subject && <li className="mono break-all">subject: {result.cert_subject}</li>}
              {result.cert_expires && <li className="mono">expires: {result.cert_expires}</li>}
              {result.pkpass_size_bytes && <li className="mono">.pkpass size: {result.pkpass_size_bytes} bytes</li>}
              {result.pass_type_id && <li className="mono">pass type: {result.pass_type_id}</li>}
            </ul>
          )}
          {result.ok && provider === "google" && (
            <ul className="mt-1 space-y-0.5 opacity-90">
              {result.client_email && <li className="mono break-all">service acct: {result.client_email}</li>}
              {result.issuer_id && <li className="mono">issuer: {result.issuer_id}</li>}
              {result.class_id && <li className="mono">class: {result.class_id}</li>}
              {result.preview_save_url_short && <li className="mono break-all">save URL: {result.preview_save_url_short}</li>}
            </ul>
          )}
          {result.note && <p className="mt-1 italic opacity-80">{result.note}</p>}
        </div>
      )}
    </div>
  );
}

// MMS image-URL reachability smoke test. Simulates EXACTLY what Twilio's
// media fetcher does (HEAD + GET against image-public, 5s timeout, Twilio
// UA). Surfaces per-stage timing so admin can spot regressions like a slow
// cold start or a misconfigured PUBLIC_BASE_URL — without firing a real
// (billed) MMS. Mounted in the Twilio P1 step row.
function MmsImageSmokeTest() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/mms-image-smoketest");
      setResult(r.data);
    } catch (e) {
      setResult({
        ok: false,
        verdict: e?.response?.data?.detail || e?.message || "Request failed",
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3" data-testid="mms-image-smoke">
      <Button
        variant="outline"
        size="sm"
        onClick={run}
        disabled={busy}
        className="rounded-xl border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5 text-xs"
        data-testid="mms-image-smoke-btn"
        title="Simulate Twilio's MMS image fetch to verify the card-image URL is fast and publicly reachable"
      >
        {busy ? "Testing…" : "Test MMS image URL reachability"}
      </Button>
      {result && (
        <div
          className={`mt-2 rounded-lg border p-3 text-xs ${
            result.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-rose-50 border-rose-200 text-rose-900"
          }`}
          data-testid="mms-image-smoke-result"
        >
          <p className="font-semibold mb-1">{result.verdict}</p>
          {result.head && (
            <p className="mono opacity-90">
              HEAD: HTTP {result.head.status_code || "—"} · {result.head.elapsed_ms ?? "—"}ms · {result.head.content_type || "no content-type"} · {result.head.content_length || 0} bytes
            </p>
          )}
          {result.get && (
            <p className="mono opacity-90">
              GET:&nbsp; HTTP {result.get.status_code || "—"} · {result.get.elapsed_ms ?? "—"}ms · {result.get.content_type || "no content-type"} · {result.get.bytes || 0} bytes {result.get.first_bytes_ok ? "· valid PNG" : ""}
            </p>
          )}
          {typeof result.length_match === "boolean" && (
            <p className="mono opacity-90">length_match: {String(result.length_match)}</p>
          )}
          {typeof result.cache_present === "boolean" && (
            <p className="mono opacity-90">
              cache: {result.cache_present ? `present (${result.cache_size_bytes || 0} bytes)` : "MISS · falls back to live render"}
            </p>
          )}
          {result.public_base_url && (
            <p className="mono break-all opacity-90">PUBLIC_BASE_URL: {result.public_base_url}</p>
          )}
          {result.note && <p className="mt-1 italic opacity-80">{result.note}</p>}
        </div>
      )}
    </div>
  );
}

// One-click synthetic dry-run of the receiver tap-to-pay journey: hits
// POST /admin/test-scan which validates qr_payload shape, PNG render,
// /api/track-card resolution, and the /pay HTML route — never charges
// real money so it's safe to run in production live mode after each deploy.
function TestScanPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/test-scan");
      setResult(r.data);
    } catch (e) {
      setResult({
        ok: false,
        verdict: e?.response?.data?.detail || e?.message || "Request failed",
        steps: [],
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3" data-testid="test-scan">
      <Button
        variant="outline"
        size="sm"
        onClick={run}
        disabled={busy}
        className="rounded-xl border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5 text-xs"
        data-testid="test-scan-btn"
        title="Synthetic end-to-end check of the receiver tap-to-pay journey. Safe in production — never charges."
      >
        {busy ? "Running test scan…" : "Run test scan (receiver flow)"}
      </Button>
      {result && (
        <div
          className={`mt-2 rounded-lg border p-3 text-xs ${
            result.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-rose-50 border-rose-200 text-rose-900"
          }`}
          data-testid="test-scan-result"
        >
          <p className="font-semibold mb-2">
            {result.ok ? "✓" : "✗"} {result.verdict}
          </p>
          {result.tested_card_last4 && (
            <p className="opacity-80 mb-2">
              Tested card: ••••{result.tested_card_last4} — receiver: {result.tested_receiver}
            </p>
          )}
          {Array.isArray(result.steps) && result.steps.length > 0 && (
            <ol className="space-y-1.5">
              {result.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`test-scan-step-${s.name}`}>
                  <span aria-hidden className={s.ok ? "text-emerald-600" : "text-rose-600"}>
                    {s.ok ? "✓" : "✗"}
                  </span>
                  <span className="flex-1 leading-relaxed">
                    <span className="font-semibold mono">{s.name}</span>
                    {s.detail ? <span className="opacity-80"> — {s.detail}</span> : null}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

// One-click rewrite of every active card's `qr_payload` to use the current
// PUBLIC_BASE_URL. Hits POST /admin/regenerate-qr-payloads. The endpoint
// also invalidates the rendered PNG cache so the next MMS/email ships the
// updated QR. Mounted on the base_url P0 row because that's the env var
// whose change typically breaks legacy QR codes (e.g. preview -> prod).
function QrPayloadRegenerator() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/regenerate-qr-payloads");
      setResult({ ok: true, ...r.data });
    } catch (e) {
      setResult({
        ok: false,
        error: e?.response?.data?.detail || e?.message || "Request failed",
      });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="mt-3" data-testid="qr-regen">
      {!confirming ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setConfirming(true)}
          disabled={busy}
          className="rounded-xl border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5 text-xs"
          data-testid="qr-regen-btn"
          title="Rewrite every active card's QR payload to point at the current PUBLIC_BASE_URL. Run this after switching from preview to your production domain."
        >
          Regenerate QR payloads
        </Button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-[#4A5C50]">
            Rewrite every active card's QR to current PUBLIC_BASE_URL?
          </span>
          <Button
            size="sm"
            onClick={run}
            disabled={busy}
            className="rounded-xl bg-[#184A30] hover:bg-[#0F3220] text-white text-xs"
            data-testid="qr-regen-confirm-btn"
          >
            {busy ? "Rewriting…" : "Yes, rewrite all"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirming(false)}
            disabled={busy}
            className="rounded-xl border-[#4A5C50]/30 text-[#4A5C50] text-xs"
            data-testid="qr-regen-cancel-btn"
          >
            Cancel
          </Button>
        </div>
      )}
      {result && (
        <div
          className={`mt-2 rounded-lg border p-3 text-xs ${
            result.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-rose-50 border-rose-200 text-rose-900"
          }`}
          data-testid="qr-regen-result"
        >
          {result.ok ? (
            <>
              <p className="font-semibold mb-1">
                ✓ Updated {result.summary?.updated ?? 0} of {result.summary?.scanned ?? 0} cards
                {result.summary?.skipped ? ` · ${result.summary.skipped} skipped` : ""}
              </p>
              {result.base_url && (
                <p className="mono break-all opacity-90">target base: {result.base_url}</p>
              )}
              <p className="opacity-80 mt-1 italic">
                Rendered card images were also invalidated — next MMS/email will ship the new QR.
              </p>
            </>
          ) : (
            <>
              <p className="font-semibold mb-1">✗ Failed to regenerate</p>
              <p className="mono break-all opacity-90">{result.error}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SetupChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deep, setDeep] = useState(null);
  const [deepLoading, setDeepLoading] = useState(false);
  const [runAll, setRunAll] = useState({ busy: false, results: null });
  const [tfv, setTfv] = useState(null);
  const [wiseBalance, setWiseBalance] = useState(null);
  const [wiseBalanceLoading, setWiseBalanceLoading] = useState(false);
  const [stripeBalance, setStripeBalance] = useState(null);
  const [stripeBalanceLoading, setStripeBalanceLoading] = useState(false);
  const [carrierBlocks, setCarrierBlocks] = useState(null);
  const [waitlist, setWaitlist] = useState(null);

  const refreshWiseBalance = useCallback(async () => {
    setWiseBalanceLoading(true);
    try {
      const r = await api.post("/admin/wise/check-balance");
      setWiseBalance(r.data);
    } catch {
      setWiseBalance({ ok: false, alert_skipped_reason: "endpoint error" });
    } finally {
      setWiseBalanceLoading(false);
    }
  }, []);

  const refreshStripeBalance = useCallback(async () => {
    setStripeBalanceLoading(true);
    try {
      const r = await api.post("/admin/stripe/check-balance");
      setStripeBalance(r.data);
    } catch {
      setStripeBalance({ ok: false, skipped_reason: "endpoint error" });
    } finally {
      setStripeBalanceLoading(false);
    }
  }, []);

  const refreshCarrierBlocks = useCallback(async () => {
    try {
      const r = await api.get("/admin/carrier-block-stats");
      setCarrierBlocks(r.data);
    } catch {
      setCarrierBlocks({ total_7d: 0, daily: [], by_error_code: {}, latest: null, error: true });
    }
  }, []);

  const refreshWaitlist = useCallback(async () => {
    try {
      const r = await api.get("/admin/waitlist/tap-to-pay");
      setWaitlist(r.data);
    } catch {
      setWaitlist({ total: 0, last_7d: 0, latest: [], error: true });
    }
  }, []);

  const runAllSmokeTests = async () => {
    // Each row: {key, label, ok, detail, elapsed_ms}. Renders sequentially
    // so the admin sees progress; failures don't short-circuit the chain.
    setRunAll({ busy: true, results: [] });
    const append = (row) =>
      setRunAll((prev) => ({ busy: true, results: [...(prev.results || []), row] }));
    const time = async (fn) => {
      const t0 = performance.now();
      try {
        const out = await fn();
        return { ok: true, out, elapsed_ms: Math.round(performance.now() - t0) };
      } catch (e) {
        return {
          ok: false,
          out: e?.response?.data || { error: e?.message || "request failed" },
          elapsed_ms: Math.round(performance.now() - t0),
        };
      }
    };

    // 1. Deep upstream check (mongo, stripe, resend, twilio in one shot)
    {
      const r = await time(() => api.get("/health/deep").then((x) => x.data));
      const checks = r.out?.checks || {};
      const subRows = Object.entries(checks).map(([name, c]) => ({
        key: `deep_${name}`,
        label: `Deep · ${name}`,
        ok: !!c.ok,
        detail: c.ok
          ? c.mode
            ? `${c.mode} mode`
            : "reachable"
          : c.error === "not_configured"
          ? "not configured"
          : c.error || "error",
        elapsed_ms: null,
      }));
      // Group elapsed_ms onto the first row of this batch so total time is visible.
      if (subRows[0]) subRows[0].elapsed_ms = r.elapsed_ms;
      subRows.forEach(append);
    }

    // 2. Apple Wallet smoke
    {
      const r = await time(() => api.post("/admin/wallet/test-apple").then((x) => x.data));
      append({
        key: "wallet_apple",
        label: "Apple Wallet · mint test pass",
        ok: !!r.out?.ok,
        detail: r.out?.ok
          ? `cert valid · ${r.out.pkpass_size_bytes || "?"} bytes`
          : `stage=${r.out?.stage || "?"} · ${r.out?.error || (r.out?.missing ? `missing: ${r.out.missing.join(", ")}` : "failed")}`,
        elapsed_ms: r.elapsed_ms,
      });
    }

    // 3. Google Wallet smoke
    {
      const r = await time(() => api.post("/admin/wallet/test-google").then((x) => x.data));
      append({
        key: "wallet_google",
        label: "Google Wallet · sign JWT",
        ok: !!r.out?.ok,
        detail: r.out?.ok
          ? `${r.out.client_email || "service account"} signs OK`
          : `stage=${r.out?.stage || "?"} · ${r.out?.error || (r.out?.missing ? `missing: ${r.out.missing.join(", ")}` : "failed")}`,
        elapsed_ms: r.elapsed_ms,
      });
    }

    // 4. MMS image-URL reachability
    {
      const r = await time(() => api.post("/admin/mms-image-smoketest").then((x) => x.data));
      append({
        key: "mms_image",
        label: "MMS image · HEAD+GET reachability",
        ok: !!r.out?.ok,
        detail: r.out?.verdict?.replace(/^(PASS|FAIL|TIMEOUT|FETCH_ERROR)[\s—-]*/, "") || "failed",
        elapsed_ms: r.elapsed_ms,
      });
    }

    setRunAll((prev) => ({ busy: false, results: prev.results }));
  };

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const r = await api.get("/admin/config-health");
      setData(r.data);
    } catch (e) {
      // If admin not logged in or endpoint fails, bounce to login.
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  const runDeepCheck = async () => {
    setDeepLoading(true);
    try {
      const r = await api.get("/health/deep");
      setDeep(r.data);
    } catch (e) {
      setDeep({ ok: false, error: e?.message || "Deep check failed" });
    } finally {
      setDeepLoading(false);
    }
  };

  useEffect(() => { load(false); }, [load]);

  // Fetch Twilio Toll-Free Verification status once on mount so we can surface
  // a red banner the moment the page opens. Silent failure — if the call dies
  // we just hide the banner (the rest of the page still works).
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/twilio-tfv-status")
      .then((r) => { if (!cancelled) setTfv(r.data); })
      .catch(() => { if (!cancelled) setTfv(null); });
    refreshWiseBalance();
    refreshStripeBalance();
    refreshCarrierBlocks();
    refreshWaitlist();
    return () => { cancelled = true; };
  }, [refreshWiseBalance, refreshStripeBalance, refreshCarrierBlocks, refreshWaitlist]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center">
        <p className="text-sm text-[#4A5C50]">Loading setup checklist…</p>
      </div>
    );
  }

  if (!data) return null;

  const byKey = Object.fromEntries(data.checks.map((c) => [c.key, c]));
  const groupProgress = (g) => {
    const items = g.keys.map((k) => byKey[k]).filter(Boolean);
    if (items.length === 0) return { ok: 0, total: 0, percent: 0 };
    const ok = items.filter((c) => c.status === "ok").length;
    return { ok, total: items.length, percent: Math.round((ok / items.length) * 100) };
  };

  const overallTotal = data.checks.length;
  const overallOk = data.summary.ok;
  const overallPct = overallTotal ? Math.round((overallOk / overallTotal) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/admin" className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] link-ul" data-testid="setup-back-to-admin">
          ← Back to admin
        </Link>

        <header className="mt-6 flex items-center gap-4">
          <span className="w-12 h-12 rounded-full bg-[#184A30] text-white inline-flex items-center justify-center">
            <Shield size={22} weight="duotone" />
          </span>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Onboarding</p>
            <h1 className="serif text-4xl">Setup checklist</h1>
            <p className="text-sm text-[#4A5C50] mt-1">
              A guided walk-through of every external integration Zcardon depends on. Green means it's wired up.
            </p>
          </div>
        </header>

        {tfv && tfv.severity === "critical" && (
          <div
            className="mt-6 rounded-2xl border-2 border-rose-500 bg-rose-50 p-5"
            data-testid="tfv-critical-banner"
          >
            <div className="flex items-start gap-3">
              <span className="shrink-0 mt-0.5">
                <XCircle size={28} weight="fill" className="text-rose-600" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.3em] uppercase text-rose-700 font-semibold">
                  Blocking · Twilio Toll-Free Verification
                </p>
                <p className="serif text-xl text-rose-900 mt-1" data-testid="tfv-banner-title">
                  {tfv.status === "TWILIO_REJECTED"
                    ? `TFV rejected by Twilio — backend SMS to US numbers is blocked`
                    : tfv.status === "NOT_SUBMITTED"
                      ? "TFV not submitted — backend SMS to US numbers is blocked"
                      : `TFV status: ${tfv.status}`}
                </p>
                {tfv.rejection_reason && (
                  <p className="text-sm text-rose-800 mt-2 mono" data-testid="tfv-rejection-reason">
                    Reason: <span className="font-semibold">{tfv.rejection_reason}</span>
                  </p>
                )}
                <p className="text-sm text-rose-800 mt-2 leading-relaxed">
                  {tfv.message}
                </p>
                <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                  {tfv.phone_number && (
                    <div>
                      <dt className="text-rose-600 uppercase tracking-widest">Sender</dt>
                      <dd className="mono text-rose-900">{tfv.phone_number}</dd>
                    </div>
                  )}
                  {tfv.business_name && (
                    <div>
                      <dt className="text-rose-600 uppercase tracking-widest">Business</dt>
                      <dd className="text-rose-900 truncate" title={tfv.business_name}>{tfv.business_name}</dd>
                    </div>
                  )}
                  {tfv.submitted_at && (
                    <div>
                      <dt className="text-rose-600 uppercase tracking-widest">Submitted</dt>
                      <dd className="text-rose-900">{tfv.submitted_at.slice(0, 10)}</dd>
                    </div>
                  )}
                  {tfv.updated_at && (
                    <div>
                      <dt className="text-rose-600 uppercase tracking-widest">Updated</dt>
                      <dd className="text-rose-900">{tfv.updated_at.slice(0, 10)}</dd>
                    </div>
                  )}
                </dl>
                <div className="mt-4 flex flex-wrap gap-2">
                  {tfv.console_url && (
                    <a
                      href={tfv.console_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white text-sm rounded-xl px-4 py-2 transition-colors"
                      data-testid="tfv-banner-fix-btn"
                    >
                      Fix in Twilio Console <ArrowSquareOut size={14} weight="bold" />
                    </a>
                  )}
                  <Link
                    to="/admin/twilio-tfv-guide"
                    className="inline-flex items-center gap-2 border border-rose-600 text-rose-700 hover:bg-rose-100 text-sm rounded-xl px-4 py-2 transition-colors"
                    data-testid="tfv-banner-guide-btn"
                  >
                    See the use-case template
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {tfv && tfv.severity === "warning" && (
          <div
            className="mt-6 rounded-2xl border border-amber-400 bg-amber-50 p-4 flex items-start gap-3"
            data-testid="tfv-warning-banner"
          >
            <Warning size={22} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-amber-900">
              <p className="font-semibold">TFV {tfv.status?.toLowerCase().replace("_", " ")}</p>
              <p className="text-amber-800 mt-0.5">{tfv.message}</p>
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                {tfv.console_url && (
                  <a
                    href={tfv.console_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline"
                    data-testid="tfv-warning-link"
                  >
                    Open submission in Twilio Console →
                  </a>
                )}
                <Link
                  to="/admin/twilio-10dlc-guide"
                  className="text-xs underline text-amber-900 font-semibold"
                  data-testid="tfv-10dlc-alt-link"
                >
                  Tired of waiting? Switch to 10DLC (~15 min setup) →
                </Link>
              </div>
            </div>
          </div>
        )}

        {tfv && tfv.severity === "ok" && (
          <div
            className="mt-6 rounded-2xl border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-3"
            data-testid="tfv-ok-banner"
          >
            <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />
            <p className="text-sm text-emerald-900">
              ✓ Twilio Toll-Free Verification approved — backend SMS to US/CA works.
            </p>
          </div>
        )}

        {/* Wise USD balance tile — surfaces our funding-rail health right
            beside the Twilio banner so the operator sees both critical
            external dependencies in one glance. */}
        {wiseBalance && wiseBalance.ok && (
          <div
            className={`mt-4 rounded-2xl border p-4 ${
              wiseBalance.below_threshold
                ? "border-amber-400 bg-amber-50"
                : "border-[#E5E0D8] bg-white"
            }`}
            data-testid="wise-balance-tile"
          >
            <div className="flex items-start gap-3">
              <span
                className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                  wiseBalance.below_threshold ? "bg-amber-100 text-amber-700" : "bg-[#184A30] text-white"
                }`}
              >
                <CurrencyDollar size={20} weight="duotone" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
                  Wise USD balance · {wiseBalance.below_threshold ? "Below threshold" : "Healthy"}
                </p>
                <p className="serif text-2xl mt-0.5 text-[#112318]" data-testid="wise-balance-amount">
                  ${(wiseBalance.balance_usd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <span className="text-xs text-[#4A5C50] ml-2">
                    threshold ${(wiseBalance.threshold_usd ?? 0).toLocaleString()}
                  </span>
                </p>
                {wiseBalance.below_threshold && (
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed" data-testid="wise-balance-warning">
                    New international payouts will land in <span className="mono">AWAITING_MANUAL_FUNDING</span>{" "}
                    until you top up. {wiseBalance.alert_sent ? "Daily email alert sent ✓" : ""}
                  </p>
                )}
                {!wiseBalance.below_threshold && (
                  <p className="text-xs text-[#4A5C50] mt-1">
                    Funding rail is healthy. Daily auto-check runs at boot + every 24 h.
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={refreshWiseBalance}
                disabled={wiseBalanceLoading}
                className="rounded-xl border-[#184A30] text-[#184A30] text-xs"
                data-testid="wise-balance-refresh"
              >
                {wiseBalanceLoading ? "Checking…" : "Refresh"}
              </Button>
            </div>
          </div>
        )}
        {wiseBalance && !wiseBalance.ok && (
          <div
            className="mt-4 rounded-2xl border border-[#E5E0D8] bg-white p-3 flex items-center gap-3 text-sm text-[#4A5C50]"
            data-testid="wise-balance-unavailable"
          >
            <CurrencyDollar size={18} weight="duotone" className="text-[#4A5C50]" />
            <span className="flex-1">
              Wise balance check unavailable
              {wiseBalance.alert_skipped_reason ? ` — ${wiseBalance.alert_skipped_reason}` : ""}
            </span>
            <Button size="sm" variant="outline" onClick={refreshWiseBalance} disabled={wiseBalanceLoading} className="rounded-xl text-xs">
              {wiseBalanceLoading ? "…" : "Retry"}
            </Button>
          </div>
        )}

        {/* Stripe → Wise sweep reminder tile — paired with the Wise tile
            above so operators see both sides of the funding rail. */}
        {stripeBalance && stripeBalance.ok && (
          <div
            className={`mt-4 rounded-2xl border p-4 ${
              stripeBalance.above_threshold
                ? "border-[#CBA35C] bg-[#CBA35C]/10"
                : "border-[#E5E0D8] bg-white"
            }`}
            data-testid="stripe-sweep-tile"
          >
            <div className="flex items-start gap-3">
              <span
                className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                  stripeBalance.above_threshold ? "bg-[#CBA35C]/30 text-[#8C6B21]" : "bg-[#635BFF] text-white"
                }`}
              >
                <CurrencyDollar size={20} weight="duotone" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
                  Stripe USD · {stripeBalance.above_threshold ? "Sweep to Wise" : "Below threshold"}
                </p>
                <p className="serif text-2xl mt-0.5 text-[#112318]" data-testid="stripe-sweep-amount">
                  ${(stripeBalance.available_usd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <span className="text-xs text-[#4A5C50] ml-2">
                    threshold ${(stripeBalance.threshold_usd ?? 0).toLocaleString()}
                  </span>
                </p>
                {stripeBalance.above_threshold && (
                  <p className="text-xs text-[#8C6B21] mt-1 leading-relaxed" data-testid="stripe-sweep-warning">
                    Time to move surplus into your Wise USD jar so international payouts have funding.
                    {stripeBalance.reminder_sent ? " Reminder email sent ✓" : ""}
                  </p>
                )}
                {!stripeBalance.above_threshold && (
                  <p className="text-xs text-[#4A5C50] mt-1">
                    Daily check runs at boot + every 24 h. Reminder fires when balance crosses threshold.
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1.5 items-stretch">
                <Link
                  to="/admin/stripe-to-wise-guide"
                  className="rounded-xl border border-[#184A30] text-[#184A30] text-xs px-3 py-1.5 hover:bg-[#184A30]/5 text-center"
                  data-testid="stripe-sweep-open-guide"
                >
                  Guide →
                </Link>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={refreshStripeBalance}
                  disabled={stripeBalanceLoading}
                  className="rounded-xl border-[#184A30] text-[#184A30] text-xs"
                  data-testid="stripe-sweep-refresh"
                >
                  {stripeBalanceLoading ? "Checking…" : "Refresh"}
                </Button>
              </div>
            </div>
          </div>
        )}
        {stripeBalance && !stripeBalance.ok && (
          <div
            className="mt-4 rounded-2xl border border-[#E5E0D8] bg-white p-3 flex items-center gap-3 text-sm text-[#4A5C50]"
            data-testid="stripe-sweep-unavailable"
          >
            <CurrencyDollar size={18} weight="duotone" className="text-[#4A5C50]" />
            <span className="flex-1">
              Stripe balance check unavailable
              {stripeBalance.skipped_reason ? ` — ${stripeBalance.skipped_reason}` : ""}
            </span>
            <Link
              to="/admin/stripe-to-wise-guide"
              className="rounded-xl border border-[#E5E0D8] text-xs px-3 py-1 hover:bg-[#FBF9F6]"
            >
              Guide
            </Link>
            <Button size="sm" variant="outline" onClick={refreshStripeBalance} disabled={stripeBalanceLoading} className="rounded-xl text-xs">
              {stripeBalanceLoading ? "…" : "Retry"}
            </Button>
          </div>
        )}

        {/* Carrier-block tile — surfaces dropped US SMS delivery count
            so the operator can quantify the cost of the TFV wait and
            track the moment 10DLC kicks in (count drops to zero). */}
        {carrierBlocks && (() => {
          const total = carrierBlocks.total_7d || 0;
          const daily = carrierBlocks.daily || [];
          const maxCount = Math.max(1, ...daily.map((d) => d.count));
          const hue = total > 0 ? "rose" : "emerald";
          const bg = hue === "rose" ? "border-rose-300 bg-rose-50" : "border-emerald-200 bg-emerald-50/30";
          const text = hue === "rose" ? "text-rose-800" : "text-emerald-900";
          const accent = hue === "rose" ? "bg-rose-500" : "bg-emerald-500";
          return (
            <div
              className={`mt-4 rounded-2xl border ${bg} p-4`}
              data-testid="carrier-block-tile"
            >
              <div className="flex items-start gap-3">
                <span className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${hue === "rose" ? "bg-rose-200 text-rose-700" : "bg-emerald-200 text-emerald-700"}`}>
                  <Warning size={20} weight="duotone" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-[10px] tracking-[0.3em] uppercase ${text}`}>
                    US SMS carrier-blocks · last 7 days
                  </p>
                  <div className="flex items-baseline gap-3 flex-wrap mt-0.5">
                    <p className={`serif text-2xl ${text}`} data-testid="carrier-block-count">
                      {total}
                      <span className="text-xs ml-2 opacity-70">
                        {total === 1 ? "blocked delivery" : "blocked deliveries"}
                      </span>
                    </p>
                    {/* Sparkline — 7 mini bars, one per day, color-keyed to the tile */}
                    <div className="flex items-end gap-0.5 h-7" data-testid="carrier-block-sparkline" title={daily.map((d) => `${d.date}: ${d.count}`).join("\n")}>
                      {daily.map((d) => (
                        <div
                          key={d.date}
                          className={`w-2 rounded-sm ${d.count > 0 ? accent : "bg-[#E5E0D8]"}`}
                          style={{ height: `${Math.max(15, (d.count / maxCount) * 100)}%` }}
                        />
                      ))}
                    </div>
                  </div>
                  {total === 0 && (
                    <p className={`text-xs ${text} mt-1`}>
                      Healthy — no US carrier blocks in the last 7 days. (If you haven't issued cards to US receivers, this could also just mean the path hasn't been tested.)
                    </p>
                  )}
                  {total > 0 && (
                    <p className={`text-xs ${text} mt-1 leading-relaxed`}>
                      {total} US receiver{total === 1 ? "" : "s"} couldn't be reached via backend SMS — carriers rejected the message. Senders fell back to email / native iMessage. Switch to 10DLC to unblock this rail.
                    </p>
                  )}
                  {carrierBlocks.by_error_code && Object.keys(carrierBlocks.by_error_code).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {Object.entries(carrierBlocks.by_error_code).map(([code, c]) => (
                        <span
                          key={code}
                          className="inline-flex items-center gap-1 text-[10px] mono rounded-md bg-white/70 border border-rose-200 px-1.5 py-0.5 text-rose-700"
                          title={`Error ${code}: ${c} occurrence${c === 1 ? "" : "s"}`}
                        >
                          {code} <span className="opacity-70">×{c}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 items-stretch">
                  <Link
                    to="/admin/twilio-10dlc-guide"
                    className="rounded-xl border border-[#184A30] text-[#184A30] text-xs px-3 py-1.5 hover:bg-[#184A30]/5 text-center"
                    data-testid="carrier-block-10dlc-guide"
                  >
                    Fix via 10DLC →
                  </Link>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={refreshCarrierBlocks}
                    className="rounded-xl border-[#184A30] text-[#184A30] text-xs"
                    data-testid="carrier-block-refresh"
                  >
                    Refresh
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Tap-to-pay waitlist tile — surface signups so the operator
            can quote the number when pitching Stripe Issuing reviewers. */}
        {waitlist && (
          <div
            className="mt-4 rounded-2xl border border-[#D96C4A]/30 bg-gradient-to-br from-[#FFF1E8] to-white p-4"
            data-testid="waitlist-tile"
          >
            <div className="flex items-start gap-3">
              <span className="shrink-0 w-10 h-10 rounded-xl bg-[#D96C4A]/20 text-[#B04E2F] flex items-center justify-center">
                <CreditCard size={20} weight="duotone" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#B04E2F]">
                  Tap-to-pay waitlist
                </p>
                <p className="serif text-2xl mt-0.5 text-[#112318]" data-testid="waitlist-total">
                  {(waitlist.total || 0).toLocaleString()}
                  <span className="text-xs text-[#4A5C50] ml-2">
                    {waitlist.total === 1 ? "person signed up" : "people signed up"}
                  </span>
                </p>
                <p className="text-xs text-[#4A5C50] mt-0.5">
                  {(waitlist.last_7d || 0)} new this week
                  {waitlist.total > 0 ? " · quote this in your Stripe Issuing application" : ""}
                </p>
              </div>
              <Link
                to="/admin/stripe-issuing-guide"
                className="rounded-xl border border-[#D96C4A] text-[#B04E2F] text-xs px-3 py-1.5 hover:bg-[#D96C4A]/5 text-center self-center"
                data-testid="waitlist-open-guide"
              >
                Issuing guide →
              </Link>
            </div>
          </div>
        )}

        <div className="mt-8 bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="setup-overall">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Overall</p>
              <p className="serif text-3xl mt-1">
                {overallOk}<span className="text-[#4A5C50] text-lg"> / {overallTotal} integrations live</span>
              </p>
            </div>
            <p className="serif text-4xl text-[#184A30]" data-testid="setup-overall-pct">{overallPct}%</p>
          </div>
          <div className="mt-3 h-2 rounded-full bg-[#FBF9F6] border border-[#E5E0D8] overflow-hidden">
            <div className="h-full bg-[#184A30] transition-all duration-500"
                 style={{ width: `${overallPct}%` }} />
          </div>
          <div className="mt-3 flex justify-end gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={runAllSmokeTests}
              disabled={runAll.busy}
              className="rounded-xl border-[#184A30] bg-[#184A30] text-white hover:bg-[#0F3220] hover:text-white"
              data-testid="setup-run-all-btn"
              title="Sequentially fire every integration smoke test — go/no-go gate before flipping a deployment live"
            >
              {runAll.busy ? "Running smoke tests…" : "✦ Run all smoke tests"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={runDeepCheck}
              disabled={deepLoading}
              className="rounded-xl border-[#CBA35C] text-[#8C6B21] hover:bg-[#CBA35C]/10"
              data-testid="setup-deep-check-btn"
              title="Hit each provider's API to confirm the keys actually work (not just present)"
            >
              {deepLoading ? "Pinging providers…" : "Run deep check"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(true)}
              disabled={refreshing}
              className="rounded-xl border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5"
              data-testid="setup-recheck-btn"
            >
              {refreshing ? "Re-checking…" : "Re-check now"}
            </Button>
          </div>

          {runAll.results && (
            <div className="mt-4 pt-4 border-t border-[#E5E0D8]" data-testid="setup-run-all-result">
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
                  Pre-deploy smoke tests
                </p>
                {(() => {
                  const total = runAll.results.length;
                  const ok = runAll.results.filter((r) => r.ok).length;
                  if (runAll.busy) {
                    return (
                      <p className="text-[11px] mono text-[#4A5C50]" data-testid="run-all-progress">
                        running… {total} done
                      </p>
                    );
                  }
                  const allPass = total > 0 && ok === total;
                  return (
                    <p
                      className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        allPass
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-rose-100 text-rose-800"
                      }`}
                      data-testid="run-all-verdict"
                    >
                      {allPass ? `✓ All ${total} passed · go for deploy` : `${ok} / ${total} passed · review failures`}
                    </p>
                  );
                })()}
              </div>
              <ul className="space-y-1.5">
                {runAll.results.map((row) => (
                  <li
                    key={row.key}
                    className={`rounded-lg border px-3 py-2 flex items-center gap-2 text-xs ${
                      row.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"
                    }`}
                    data-testid={`run-all-row-${row.key}`}
                  >
                    <StatusIcon status={row.ok ? "ok" : "error"} size={14} />
                    <span className="font-medium text-[#112318] min-w-0">{row.label}</span>
                    <span className="ml-2 mono opacity-80 truncate flex-1">{row.detail}</span>
                    {row.elapsed_ms != null && (
                      <span className="text-[10px] mono opacity-60 shrink-0">{row.elapsed_ms}ms</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {deep && (
            <div className="mt-4 pt-4 border-t border-[#E5E0D8]" data-testid="setup-deep-result">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-2">
                Live upstream check · pings each provider
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(deep.checks || {}).map(([name, c]) => (
                  <div
                    key={name}
                    className={`rounded-lg border px-3 py-2 flex items-center gap-2 ${
                      c.ok
                        ? "bg-emerald-50 border-emerald-200"
                        : c.error === "not_configured"
                        ? "bg-stone-50 border-stone-200"
                        : "bg-rose-50 border-rose-200"
                    }`}
                    data-testid={`setup-deep-${name}`}
                  >
                    <StatusIcon status={c.ok ? "ok" : (c.error === "not_configured" ? "warn" : "error")} size={16} />
                    <span className="font-medium capitalize text-[#112318]">{name}</span>
                    {c.ok ? (
                      <span className="ml-auto text-xs mono text-emerald-700">
                        {c.mode ? `${c.mode} mode` : "reachable"}
                      </span>
                    ) : (
                      <span className="ml-auto text-xs mono text-rose-700">
                        {c.error === "not_configured" ? "not set" : c.error}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {!deep.ok && (
                <p className="text-xs text-rose-700 mt-3" data-testid="setup-deep-warning">
                  ⚠ At least one provider returned an error — credentials may be expired or revoked.
                  The env vars are set, but the upstream API rejects them.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-10 space-y-8" data-testid="setup-groups">
          {GROUPS.map((group) => {
            const prog = groupProgress(group);
            const items = group.keys.map((k) => byKey[k]).filter(Boolean);
            return (
              <section key={group.id} data-testid={`setup-group-${group.id}`}>
                <div className={`rounded-2xl border-2 p-5 ${group.accent}`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase opacity-80">{group.label}</p>
                      <p className="text-sm mt-1 opacity-90 max-w-xl">{group.summary}</p>
                    </div>
                    <p className={`text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full ${group.chip}`}>
                      {prog.ok} / {prog.total} done
                    </p>
                  </div>
                </div>

                <ol className="mt-4 space-y-3" data-testid={`setup-list-${group.id}`}>
                  {items.map((check, i) => (
                    <li
                      key={check.key}
                      className={`bg-white border rounded-2xl p-5 flex items-start gap-4 ${
                        check.status === "ok" ? "border-[#E5E0D8] opacity-70" : "border-[#E5E0D8]"
                      }`}
                      data-testid={`setup-step-${check.key}`}
                    >
                      <StatusIcon status={check.status} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-[10px] tracking-widest uppercase text-[#4A5C50]">
                            Step {i + 1}
                          </span>
                          <h3 className="serif text-xl">{check.label}</h3>
                          <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full ${
                            check.status === "ok" ? "bg-emerald-100 text-emerald-800"
                            : check.status === "warn" ? "bg-amber-100 text-amber-800"
                            : "bg-rose-100 text-rose-800"
                          }`}>
                            {check.status === "ok" ? "live" : check.status}
                          </span>
                        </div>
                        <p className="text-sm text-[#4A5C50] mt-1">{check.note}</p>
                        <p className="text-[11px] mono text-[#4A5C50] mt-2 break-all" data-testid={`setup-value-${check.key}`}>
                          {check.value}
                        </p>
                        {check.key === "apple_wallet" && (
                          <>
                            <WalletSmokeTest provider="apple" />
                            <Link
                              to="/admin/wallet-credentials-guide"
                              className="inline-flex items-center gap-1 text-xs text-[#184A30] hover:underline mt-2"
                              data-testid="setup-apple-wallet-guide-link"
                            >
                              Step-by-step setup guide →
                            </Link>
                          </>
                        )}
                        {check.key === "google_wallet" && (
                          <>
                            <WalletSmokeTest provider="google" />
                            <Link
                              to="/admin/wallet-credentials-guide"
                              className="inline-flex items-center gap-1 text-xs text-[#184A30] hover:underline mt-2"
                              data-testid="setup-google-wallet-guide-link"
                            >
                              Step-by-step setup guide →
                            </Link>
                          </>
                        )}
                        {check.key === "twilio" && <MmsImageSmokeTest />}
                        {check.key === "base_url" && (
                          <>
                            <QrPayloadRegenerator />
                            <TestScanPanel />
                          </>
                        )}
                      </div>
                      {check.action_url && check.action_label && check.status !== "ok" && (
                        <a
                          href={check.action_url}
                          target={check.action_url.startsWith("http") || check.action_url.startsWith("mailto") ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap ${
                            check.status === "error"
                              ? "bg-[#D96C4A] text-white hover:bg-[#B04E2F]"
                              : "bg-[#184A30] text-white hover:bg-[#0F3220]"
                          }`}
                          data-testid={`setup-action-${check.key}`}
                        >
                          {check.action_label}
                          <ArrowSquareOut size={12} weight="bold" />
                        </a>
                      )}
                    </li>
                  ))}
                </ol>
              </section>
            );
          })}
        </div>

        <div className="mt-12 bg-[#112318] text-[#FBF9F6] rounded-2xl p-6" data-testid="setup-footer-cta">
          <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">When everything's green</p>
          <p className="serif text-2xl mt-1">You're production-ready.</p>
          <p className="text-sm opacity-70 mt-2 max-w-xl">
            Once all P0 + P1 items are live, you can confidently flip <span className="mono text-[#8BA888]">PUBLIC_BASE_URL</span> to your custom domain and start accepting real customers. P2 items can be enabled at any time without downtime.
          </p>
          <Link to="/admin" className="inline-block mt-4 text-xs uppercase tracking-widest underline underline-offset-4 hover:text-[#CBA35C]"
                data-testid="setup-jump-overview">
            Back to admin overview →
          </Link>
        </div>
      </div>
    </div>
  );
}
